import React from 'react';

// ==================== ACTIVELY USED FUNCTIONS ====================

/**
 * Formats a number with thousand separators (commas)
 * @param {number|string} value - The number to format
 * @returns {string} Formatted number with commas
 */
export const formatNumberWithDot = (value) => {
  if (value == null) return '0';
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * Formats a rank with ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
 * @param {number} rank - The rank number to format
 * @returns {JSX.Element} Formatted rank with superscripted ordinal suffix
 */
export const formatRank = (rank) => {
  if (!rank) return '';
  const suffix = ['th', 'st', 'nd', 'rd'][rank % 10 > 3 || Math.floor(rank % 100 / 10) === 1 ? 0 : rank % 10];
  return (
    <span>
      {rank}
      <sup>{suffix}</sup>
    </span>
  );
};

/**
 * Sorts data array based on configuration
 * @param {Array} data - Array of objects to sort
 * @param {Object} sortConfig - Configuration object with key and direction
 * @returns {Array} Sorted array
 */
export const sortData = (data, sortConfig) => {
  if (!Array.isArray(data)) {
    console.warn('sortData: Expected array input, received:', typeof data);
    return [];
  }

  const { key, direction } = sortConfig;

  return [...data].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    // Handle null/undefined values
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return direction === 'ascending' ? -1 : 1;
    if (bVal == null) return direction === 'ascending' ? 1 : -1;

    // Handle numbers
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'ascending' ? aVal - bVal : bVal - aVal;
    }

    // Handle strings
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return direction === 'ascending'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    return 0;
  });
};

/**
 * Finds the highest numeric value in a dataset for a specific property
 * @param {Array} data - Array of objects
 * @param {string} propertyName - Property name to find max value for
 * @returns {number} Maximum value found
 */
export const findHighestValue = (data, propertyName) => {
  if (!Array.isArray(data) || data.length === 0) return 0;
  
  return data.reduce((maxValue, item) => {
    const currentValue = item[propertyName];
    return (typeof currentValue === 'number' && currentValue > maxValue) 
      ? currentValue 
      : maxValue;
  }, 0);
};

/**
 * Generates Henley Global flag image URL
 * @param {string} iso - ISO2 country code
 * @returns {string} Flag image URL
 */
export const henleyFlagLink = (iso) => {
  if (!iso || iso === 'None') return '';
  return `https://cdn.henleyglobal.com/themes/hgo/public/assets/img/flags/${slugify(iso)}.png`;
};

/**
 * Converts string to URL-friendly slug
 * @param {string} str - String to slugify
 * @returns {string} URL-friendly slug
 */
export const slugify = (str) => {
  if (!str) return '';

  return str
    .toString()
    .normalize('NFKD')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');
};

/**
 * Gets longitude and latitude for a country code from geographic features data
 * @param {string} countryCode - ISO2 country code to lookup
 * @param {Array} features - Array of geographic feature objects with properties
 * @returns {Object|undefined} Object with country, longitude, latitude or undefined if not found
 */
export const getLonAndLat = (countryCode, features) => {
  let dataset = [];
  for(let i = 0; i <= features.length; i++){

    if(features[i] && features[i].properties.ISO2){
      let name = features[i].properties.ISO2;
      let longitude = features[i].properties.LON;
      let latitude = features[i].properties.LAT;
      let country = features[i].properties.NAME

      dataset[name] = {
        country: country,
        longitude: longitude,
        latitude: latitude
      }
    }
  }

  return dataset[countryCode];
};

/**
 * Converts hex color to RGB array
 * @param {string} hex - Hex color value (with or without #)
 * @returns {Array} RGB values as [r, g, b]
 */
export const hexToRgb = (hex) => {
  // Remove the # character if present
  hex = hex.replace("#", "");

  // Parse the hexadecimal value to decimal
  var r = parseInt(hex.substring(0, 2), 16);
  var g = parseInt(hex.substring(2, 4), 16);
  var b = parseInt(hex.substring(4, 6), 16);

  // Return the RGB values in an array
  return [r, g, b];
};

/**
 * Renders HTML string as React element
 * @param {string} htmlString - HTML string to render
 * @returns {React.Element} React element with dangerouslySetInnerHTML
 */
export const renderHTML = (htmlString) => {
  return React.createElement('span', { dangerouslySetInnerHTML: { __html: htmlString } });
};

// ==================== LEGACY/UNUSED FUNCTIONS (kept for compatibility) ====================

/**
 * @deprecated - Consider using a more specific ordinal function if needed
 */
export const getNumberWithOrdinalWithSuperscript = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return (
    <span>
      {n}
      <sup>{s[(v - 20) % 10] || s[v] || s[0]}</sup>
    </span>
  );
};

/**
 * @deprecated - Consider using modern CSS or next/image if needed
 */
export const imageLink = (name) => {
  return `./images/${slugify(name)}.jpg`;
};

/**
 * @deprecated - Consider using henleyFlagLink instead
 */
export const flag = (name) => {
  return `./flags/${slugify(name)}.png`;
};

/**
 * @deprecated - Use Array.prototype.reduce directly for better performance
 */
export const groupBy = (prop) => (data) => {
  return data.reduce((dict, item) => {
    const { [prop]: _, ...rest } = item;
    dict[item[prop]] = [...(dict[item[prop]] || []), rest];
    return dict;
  }, {});
};

/**
 * @deprecated - Legacy sorting function, use sortData instead
 */
export function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

/**
 * @deprecated - Legacy sorting function, use sortData instead
 */
export function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 * @deprecated - Legacy sorting function with search, use sortData + filter instead
 */
export function applySortFilter({ data, comparator, searchTerm, filterColumn }) {
  const stabilizedData = data.map((el, index) => [el, index]);
  stabilizedData.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    return (order !== 0) ? order : (a[1] - b[1]);
  });
  data = stabilizedData.map((el) => el[0]);

  if (searchTerm) {
    data = data.filter((item) => (
      item[filterColumn].toLowerCase() === searchTerm.toLowerCase()
    ));
  }

  return data;
}

/**
 * @deprecated - Consider using toFixed directly where needed
 */
export function percentageFixed(input) {
  return (input * 100).toFixed(2);
}

/**
 * @deprecated - Consider using getNumberWithOrdinalWithSuperscript instead
 */
export function rankSuffix(rank) {
  const j = rank % 10;
  const k = rank % 100;
  if (j === 1 && k !== 11) return (<span>{rank}<sup>st</sup></span>);
  if (j === 2 && k !== 12) return (<span>{rank}<sup>nd</sup></span>);
  if (j === 3 && k !== 13) return (<span>{rank}<sup>rd</sup></span>);
  return (<span>{rank}<sup>th</sup></span>);
}